export const AGREED_DELTA_FIELD = {
  DELTA_T: "delta_t",
  SUGGESTED_DELTA_T: "suggested_delta_t",
  SUGGESTED_DELTA_UNIT: "suggested_delta_unit",
  DELTA_QUANTITY_T: "delta_quantity_t",
  DELTA_UNIT: "delta_unit",
};

export const AGREED_DELTA_UNIT = {
  CD: "cd",
  PD: "pd",
};

export const AGREED_DELTA_UNIT_LABEL = {
  [AGREED_DELTA_UNIT.CD]: "call delta",
  [AGREED_DELTA_UNIT.PD]: "premium delta",
};

export const AGREED_DELTA_SIDE = {
  BID: "bid",
  ASK: "ask",
};

export const AGREED_DELTA_LABELS = {
  SECTION: "Agreed Delta",
  RANGE_HINT: "Enter a number from 0 to 100",
  UNDERLYING: "Underlying",
};

// Same order as Trade hedge rows (qty, then delta). Flip `visible` when qty is wired.
export const AGREED_DELTA_FIELDS = [
  {
    key: AGREED_DELTA_FIELD.DELTA_QUANTITY_T,
    label: "Delta Qty (t)",
    placeholder: "0",
    visible: false,
  },
  {
    key: AGREED_DELTA_FIELD.DELTA_T,
    label: "Delta (t)",
    placeholder: "5",
    visible: true,
    hint: AGREED_DELTA_LABELS.RANGE_HINT,
  },
];

export function visibleAgreedDeltaFields() {
  return AGREED_DELTA_FIELDS.filter((field) => field.visible);
}

/** Row title: backend `label`, or underlying + tenor so the input is never unlabeled. */
export function agreedDeltaGroupTitle(group) {
  const label = String(group?.label ?? "").trim();
  if (label) return label;
  return (
    [group?.underlying, group?.underlying_tenor]
      .map((part) => String(part ?? "").trim())
      .filter(Boolean)
      .join(" ") || "—"
  );
}

export function parseAgreedDeltaNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function isValidAgreedDeltaNumber(value) {
  const n = parseAgreedDeltaNumber(value);
  return n != null && n >= 0 && n <= 100;
}

/** Missing or unknown units display as cd. pd is only an explicit label. */
export function displayedAgreedDeltaUnit(unit) {
  const normalized = String(unit ?? "").trim().toLowerCase();
  return normalized === AGREED_DELTA_UNIT.PD
    ? AGREED_DELTA_UNIT.PD
    : AGREED_DELTA_UNIT.CD;
}

export function otherAgreedDeltaUnit(unit) {
  return displayedAgreedDeltaUnit(unit) === AGREED_DELTA_UNIT.CD
    ? AGREED_DELTA_UNIT.PD
    : AGREED_DELTA_UNIT.CD;
}

/**
 * Clicking the suffix agrees a different delta. The number and the
 * calculated suggestion stay as loaded; the backend converts on save.
 */
export function toggleAgreedDeltaUnit(group) {
  return {
    ...group,
    [AGREED_DELTA_FIELD.DELTA_UNIT]: otherAgreedDeltaUnit(
      group?.[AGREED_DELTA_FIELD.DELTA_UNIT]
    ),
  };
}

/** A side line. An empty number is hidden. The line stays even when the input already matches it. */
function agreedDeltaSideReading(text, unit) {
  const raw = String(text ?? "").trim();
  if (raw === "") return null;
  if (parseAgreedDeltaNumber(raw) == null) return null;

  return { text: raw, unit: displayedAgreedDeltaUnit(unit) };
}

/**
 * Bid above ask. Each line is that side's calculated greek.
 * An empty side is omitted, and the other side is left as sent.
 */
export function agreedDeltaSideSuggestions(group) {
  const bid = agreedDeltaSideReading(
    group?.suggested_bid_delta_t,
    group?.suggested_bid_delta_unit
  );
  const ask = agreedDeltaSideReading(
    group?.suggested_ask_delta_t,
    group?.suggested_ask_delta_unit
  );

  return [
    bid && { side: AGREED_DELTA_SIDE.BID, ...bid },
    ask && { side: AGREED_DELTA_SIDE.ASK, ...ask },
  ].filter(Boolean);
}

export function applyAgreedDeltaReading(group, reading) {
  if (!reading?.text) return group;
  return {
    ...group,
    [AGREED_DELTA_FIELD.DELTA_T]: reading.text,
    [AGREED_DELTA_FIELD.DELTA_UNIT]: displayedAgreedDeltaUnit(reading.unit),
  };
}

export function applyAgreedDeltaReadingAt(groups, index, reading) {
  return (groups ?? []).map((group, groupIndex) =>
    groupIndex === index ? applyAgreedDeltaReading(group, reading) : group
  );
}

export function toggleAgreedDeltaUnitAt(groups, index) {
  return (groups ?? []).map((group, groupIndex) =>
    groupIndex === index ? toggleAgreedDeltaUnit(group) : group
  );
}

export function payloadFromAgreedDeltaGroups(groups) {
  return (groups ?? []).map((group) => ({
    underlying: group.underlying,
    underlying_tenor: group.underlying_tenor ?? "",
    delta_t: String(group.delta_t ?? "").trim(),
    delta_unit: displayedAgreedDeltaUnit(group.delta_unit),
  }));
}

/** Live options have no hedge — agreed delta UI and payload apply only when not live. */
export function isLiveTeamRunOption(live) {
  return live === 1 || live === "1" || live === true;
}

const TEAM_RUN_LIVE_SHORTHAND_PATTERN = /(?:^|[\s:/])live(?:\s|:|$)/i;

/**
 * Team Run list rows mirror Pricer agg shape: `live` is often on `summary_agg`,
 * not the top-level row object used by the action menu.
 */
export function getTeamRunSummaryLive(row) {
  if (row == null || typeof row !== "object") return undefined;

  const fromFields =
    row.live ?? row.summary_agg?.live ?? row.leg_agg?.live;
  if (fromFields !== undefined && fromFields !== null && fromFields !== "") {
    return fromFields;
  }

  const shorthand =
    row.copy_shorthand ?? row.summary_agg?.copy_shorthand ?? "";
  if (
    typeof shorthand === "string" &&
    TEAM_RUN_LIVE_SHORTHAND_PATTERN.test(shorthand)
  ) {
    return 1;
  }

  return undefined;
}

export function isLiveTeamRunRow(row) {
  return isLiveTeamRunOption(getTeamRunSummaryLive(row));
}

export function agreedDeltasAreValid(groups) {
  const payload = payloadFromAgreedDeltaGroups(groups);
  if (!payload.length) return false;
  return payload.every((item) => isValidAgreedDeltaNumber(item.delta_t));
}

export function agreedDeltasValidForSubmit(live, groups) {
  if (isLiveTeamRunOption(live)) return true;
  return agreedDeltasAreValid(groups);
}

/** Omits `agreed_deltas` when live; otherwise merges the submit payload. */
export function withAgreedDeltasSubmitPayload(body, live, groups) {
  if (isLiveTeamRunOption(live)) return body;
  return {
    ...body,
    agreed_deltas: payloadFromAgreedDeltaGroups(groups),
  };
}

export function agreedDeltasChanged(current, original) {
  return (
    JSON.stringify(payloadFromAgreedDeltaGroups(current)) !==
    JSON.stringify(payloadFromAgreedDeltaGroups(original))
  );
}

export function agreedDeltasDirty(current, original, live) {
  if (isLiveTeamRunOption(live)) return false;
  return agreedDeltasChanged(current, original);
}

export function setAgreedDeltaField(groups, index, field, value) {
  return (groups ?? []).map((group, groupIndex) =>
    groupIndex === index ? { ...group, [field]: value } : group
  );
}
